import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertCircle, Pill, Search } from 'lucide-react';
import { drugsService } from '@/services/drugs';
import { getApiError } from '@/services/api';
import { EmptyState, LoadingState, PageHeader } from '@/components/ui';
import { localeFor } from '@/components/reports/chartTheme';
import { useDebouncedValue } from './useDebouncedValue';

const MIN_QUERY = 2;

export function DrugManagement() {
  const { t, i18n } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const enabled = search.length >= MIN_QUERY;
  const locale = localeFor(i18n.language);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['drugs', 'search', search],
    queryFn: () => drugsService.search({ q: search, limit: 50 }),
    enabled,
    placeholderData: keepPreviousData,
  });

  const drugs = enabled ? data?.drugs ?? [] : [];
  const formatPrice = (centimes: number) =>
    `${(centimes / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DZD`;

  return (
    <div>
      <PageHeader title={t('nav.drugs')} description={t('admin.drugs.description')} />

      {/* Search */}
      <div className="card mb-6 p-4">
        <label htmlFor="drug-search" className="sr-only">
          {t('common.search')}
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            id="drug-search"
            type="search"
            className="input ps-9"
            placeholder={t('admin.drugs.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoComplete="off"
          />
          {isFetching && enabled && (
            <span className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
          )}
        </div>
      </div>

      {/* Results */}
      <div className="card overflow-hidden p-0">
        {!enabled ? (
          <EmptyState
            icon={Search}
            title={t('admin.drugs.promptTitle')}
            description={t('admin.drugs.promptHint', { count: MIN_QUERY })}
          />
        ) : isLoading ? (
          <LoadingState />
        ) : isError ? (
          <EmptyState icon={AlertCircle} title={t('admin.loadError')} description={getApiError(error).message} />
        ) : drugs.length === 0 ? (
          <EmptyState icon={Pill} title={t('common.noResults')} description={t('admin.drugs.noMatch', { query: search })} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('drug.tradeName')}</th>
                    <th scope="col">{t('admin.drugs.dci')}</th>
                    <th scope="col">{t('drug.dosageForm')}</th>
                    <th scope="col">{t('drug.manufacturer')}</th>
                    <th scope="col">{t('admin.drugs.atc')}</th>
                    <th scope="col" className="text-end">
                      {t('admin.drugs.price')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {drugs.map((drug) => (
                    <tr key={drug.id}>
                      <td>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">{drug.tradeName}</span>
                          {drug.controlledClass && (
                            <span className="badge-warning">
                              {t(`drug.tableau${drug.controlledClass}`, {
                                defaultValue: `Tableau ${drug.controlledClass}`,
                              })}
                            </span>
                          )}
                        </div>
                        {drug.strength && <p className="text-xs text-gray-500">{drug.strength}</p>}
                      </td>
                      <td className="max-w-[14rem] break-words">{drug.dci || '—'}</td>
                      <td className="whitespace-nowrap">{drug.dosageForm || '—'}</td>
                      <td className="max-w-[12rem] truncate" title={drug.manufacturer}>
                        {drug.manufacturer || '—'}
                      </td>
                      <td className="whitespace-nowrap">
                        {drug.atcCode ? <span className="badge-gray font-mono">{drug.atcCode}</span> : '—'}
                      </td>
                      <td className="whitespace-nowrap text-end tabular-nums">
                        {drug.referencePrice != null ? formatPrice(drug.referencePrice) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-gray-100 px-4 py-2.5 text-xs text-gray-500">
              {t('admin.drugs.resultCount', { count: drugs.length })}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
